// 模块化的背景图片懒加载脚本
/**
 * 使用示例
 * <div class="bg-lazyload" data-bg-src="{{ section.settings.image | img_url: '800x800' }}"></div>
 * 已经在全局引入该js，并且在全局定义以下css
.bg-lazyload {
  opacity: 0;
  transition: opacity 0.3s ease;
}
.bg-lazy-loaded {
  opacity: 1;
}
*/

class BackgroundLazyLoader {
  constructor() {
    this.observer = null;
    this.mutationObserver = null;
    this.isInitialized = false;
  }

  // 初始化懒加载
  init() {
    if (this.isInitialized) return;
    
    // 检测浏览器是否支持IntersectionObserver
    if ('IntersectionObserver' in window) {
      // 创建IntersectionObserver实例
      this.observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // 元素进入视口，加载背景图片
            const element = entry.target;
            const imgUrl = element.getAttribute('data-bg-src');
            
            if (imgUrl) {
              // 设置背景图片
              element.style.backgroundImage = `url('${imgUrl}')`;
              // 移除data属性，避免重复加载
              element.removeAttribute('data-bg-src');
              // 为元素添加加载完成类
              element.classList.add('bg-lazy-loaded');
              // 停止观察该元素
              observer.unobserve(element);
            }
          }
        });
      }, {
        // 配置选项：当元素进入视口10%时触发
        threshold: 0.1,
        // 根边距：提前100px加载
        rootMargin: '0px 0px 100px 0px'
      });

      // 查找所有需要懒加载的背景图片元素
      const lazyBgElements = document.querySelectorAll('.bg-lazyload');
      lazyBgElements.forEach(element => {
        this.observer.observe(element);
      });

      // 监听动态添加的元素
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) {
              // 检查新添加的元素是否需要懒加载
              if (node.classList.contains('bg-lazyload')) {
                this.observer.observe(node);
              }
              // 检查新添加元素的子元素
              const childLazyElements = node.querySelectorAll('.bg-lazyload');
              childLazyElements.forEach(child => {
                this.observer.observe(child);
              });
            }
          });
        });
      });

      // 监听整个文档的变化
      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      this.isInitialized = true;
    } else {
      // 浏览器不支持IntersectionObserver，直接加载所有背景图片
      this.fallbackLoad();
    }
  }

  // 降级方案：直接加载所有背景图片
  fallbackLoad() {
    const lazyBgElements = document.querySelectorAll('.bg-lazyload');
    lazyBgElements.forEach(element => {
      const imgUrl = element.getAttribute('data-bg-src');
      if (imgUrl) {
        element.style.backgroundImage = `url('${imgUrl}')`;
        element.removeAttribute('data-bg-src');
        element.classList.add('bg-lazy-loaded');
      }
    });
    this.isInitialized = true;
  }

  // 销毁懒加载
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    this.isInitialized = false;
  }

  // 手动观察特定元素
  observeElement(element) {
    if (this.observer && element && element.classList.contains('bg-lazyload')) {
      this.observer.observe(element);
    }
  }
}

// 导出实例
const bgLazyLoader = new BackgroundLazyLoader();

// 如果在浏览器环境中，自动初始化
if (typeof window !== 'undefined') {
  window.bgLazyLoader = bgLazyLoader;
  // 页面加载完成后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bgLazyLoader.init());
  } else {
    bgLazyLoader.init();
  }
}

// 导出模块
if (typeof module !== 'undefined' && module.exports) {
  module.exports = bgLazyLoader;
}
